import { useDebouncedCallback } from 'use-debounce';

import { useGetFile } from '@/hooks/useFiles';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { queryClient } from '@/queryclient';
import { PaneState } from '@/sections/insights/hunts/PaneState';
import type { Hunt } from '@/types';

interface LogPaneProps {
  hunt: Hunt;
}

// Mirror the rest of the hunt feature: WebSocket for liveness, 60s
// fallback poll as a safety net for missed events.
const FALLBACK_POLL_MS = 60_000;
const INVALIDATE_DEBOUNCE_MS = 2_000;

export function LogPane({ hunt }: LogPaneProps) {
  const filename = `memory/hunt/${hunt.uuid}/summary.log`;
  const { data, isLoading } = useGetFile<string>(
    { name: filename },
    { enabled: !!hunt.uuid, refetchInterval: FALLBACK_POLL_MS }
  );

  const debouncedInvalidate = useDebouncedCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({ name: filename }),
      }),
    INVALIDATE_DEBOUNCE_MS
  );
  useRealtimeSubscription({
    patterns: hunt.uuid
      ? [{ pattern: `#file#${filename}`, matchType: 'exact' }]
      : [],
    enabled: !!hunt.uuid,
    onUpdate: debouncedInvalidate,
  });

  const log = typeof data === 'string' ? data : '';

  return (
    <PaneState
      isLoading={isLoading}
      isEmpty={!log}
      empty={{
        title: 'No log entries yet',
        description: 'The agent will append here as iterations finalize.',
      }}
    >
      <pre className="bg-chariot-background-secondary text-chariot-text-primary m-4 overflow-auto rounded-md p-3 text-xs whitespace-pre-wrap">
        {log}
      </pre>
    </PaneState>
  );
}
